import { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { Delete, ChefHat, ChevronDown, Check, Loader2, Building2 } from 'lucide-react';
import clsx from 'clsx';

export default function LoginPage() {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [restaurantCode, setRestaurantCode] = useState('');
  const [isLookingUpRestaurant, setIsLookingUpRestaurant] = useState(false);

  const {
    login,
    fetchRestaurantBySlug,
    selectRestaurant,
    selectedRestaurantId,
    selectedRestaurantName,
    isLoadingRestaurants,
    clearRestaurant,
    sessionExpired,
  } = useAuthStore();

  // Check for restaurant ID/slug in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const restaurantParam = params.get('r');

    if (restaurantParam) {
      // Look up restaurant by slug or ID
      fetchRestaurantBySlug(restaurantParam);
    }
  }, [fetchRestaurantBySlug]);

  const hasRestaurant = !!selectedRestaurantId;

  const handleKeyPress = async (key: string) => {
    if (!hasRestaurant) {
      setError('Please select a restaurant first');
      return;
    }

    if (key === 'clear') {
      setPin('');
      setError('');
      return;
    }

    if (key === 'delete') {
      setPin((prev) => prev.slice(0, -1));
      setError('');
      return;
    }

    if (pin.length >= 6) return;

    const newPin = pin + key;
    setPin(newPin);
    setError('');

    // Auto-submit only when PIN reaches max length (6 digits)
    // For shorter PINs, user must press Login button
    if (newPin.length === 6) {
      submitPin(newPin);
    }
  };

  const submitPin = async (pinToSubmit: string) => {
    if (pinToSubmit.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    setLoading(true);
    const result = await login(pinToSubmit);
    setLoading(false);

    if (!result.success) {
      setError(result.error || 'Invalid PIN');
      setPin('');
    }
  };

  const handleSubmit = () => {
    submitPin(pin);
  };

  const handleRestaurantCodeSubmit = async () => {
    if (!restaurantCode.trim()) {
      setError('Please enter a restaurant code');
      return;
    }
    setIsLookingUpRestaurant(true);
    setError('');
    const found = await fetchRestaurantBySlug(restaurantCode.trim());
    setIsLookingUpRestaurant(false);
    if (!found) {
      setError('Restaurant not found. Check your code and try again.');
    }
  };

  const handleChangeRestaurant = () => {
    clearRestaurant();
    setRestaurantCode('');
    setPin('');
    setError('');
    // Clear URL param
    window.history.replaceState({}, '', window.location.pathname);
  };

  const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['clear', '0', 'delete'],
  ];

  return (
    // Scrollable + 1px-taller inner wrapper = iOS rubber-band bounce even
    // though the content fits the screen
    <div className="h-[100dvh] overflow-y-auto overscroll-contain bg-gradient-to-br from-primary-900 to-primary-700">
      <div className="min-h-[calc(100%+1px)] flex items-center justify-center p-4 safe-top safe-bottom">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ChefHat className="w-10 h-10 text-white" />
            <img src="/mise-wordmark.svg" alt="mise" className="h-7 invert" />
          </div>
          <p className="text-lg font-medium text-primary-100">Point of Sale</p>
          {/* Neutral copy — you land here both to clock in AND when switching
              users mid-shift, where "clock in" would be wrong. */}
          <p className="text-primary-200 mt-1">Enter your PIN to continue</p>
        </div>

        {sessionExpired && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-white/10 text-white text-sm text-center">
            Your session ended — please sign in again to keep going.
            {localStorage.getItem('mise-pos-last-401') && (
              <span className="block mt-1 text-xs text-primary-200/70">
                ({localStorage.getItem('mise-pos-last-401')})
              </span>
            )}
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {/* Loading state */}
          {isLoadingRestaurants && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
              <span className="ml-2 text-slate-500">Loading...</span>
            </div>
          )}

          {/* Restaurant Code Entry (when no restaurant selected) */}
          {!isLoadingRestaurants && !hasRestaurant && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-slate-900">Enter Restaurant Code</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Your manager will provide this code
                </p>
              </div>

              <input
                type="text"
                value={restaurantCode}
                onChange={(e) => setRestaurantCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && handleRestaurantCodeSubmit()}
                placeholder="restaurant-code"
                className="w-full px-4 py-3 text-center text-lg font-mono bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />

              {error && (
                <p className="text-center text-danger-500 text-sm">{error}</p>
              )}

              <button
                onClick={handleRestaurantCodeSubmit}
                disabled={isLookingUpRestaurant || !restaurantCode.trim()}
                className="w-full py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700 active:scale-98 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLookingUpRestaurant ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Looking up...
                  </span>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          )}

          {/* PIN Entry (when restaurant is selected) */}
          {!isLoadingRestaurants && hasRestaurant && (
            <>
              {/* Restaurant indicator */}
              <div className="mb-6 flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl">
                <div>
                  <span className="text-xs text-slate-500 uppercase tracking-wide">Restaurant</span>
                  <p className="font-medium text-slate-900">{selectedRestaurantName}</p>
                </div>
                <button
                  onClick={handleChangeRestaurant}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Change
                </button>
              </div>

          {/* PIN dots */}
          <div className="flex justify-center gap-3 mb-6">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={clsx(
                  'w-4 h-4 rounded-full transition-all duration-150',
                  i < pin.length
                    ? 'bg-primary-600 scale-100'
                    : 'bg-slate-200 scale-90'
                )}
              />
            ))}
          </div>

          {error && (
            <p className="text-center text-danger-500 text-sm mb-4 animate-fade-in">
              {error}
            </p>
          )}

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3">
            {keys.flat().map((key) => (
              <button
                key={key}
                onClick={() => handleKeyPress(key)}
                disabled={loading || isLoadingRestaurants}
                className={clsx(
                  'h-16 rounded-xl font-semibold text-xl transition-all duration-150',
                  'active:scale-95 disabled:opacity-50',
                  key === 'clear'
                    ? 'bg-slate-100 text-slate-500 text-sm'
                    : key === 'delete'
                    ? 'bg-slate-100 text-slate-500 flex items-center justify-center'
                    : 'bg-slate-50 text-slate-900 hover:bg-slate-100 active:bg-slate-200'
                )}
              >
                {key === 'delete' ? (
                  <Delete className="w-6 h-6" />
                ) : key === 'clear' ? (
                  'Clear'
                ) : (
                  key
                )}
              </button>
            ))}
          </div>

          {/* Login button - shows when PIN is 4-5 digits */}
          {pin.length >= 4 && pin.length < 6 && !loading && (
            <button
              onClick={handleSubmit}
              className="w-full mt-4 py-4 bg-primary-600 text-white rounded-xl font-semibold text-lg hover:bg-primary-700 active:scale-98 transition-all"
            >
              Login
            </button>
          )}

          {loading && (
            <div className="mt-4 text-center text-slate-500">
              <span className="animate-pulse">Signing in...</span>
            </div>
          )}
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
